// Scoring an account's Kalshi API load against its OWN rate limit.
//
// Kalshi meters two token buckets PER ACCOUNT — read and write — so the only
// meaningful number is each account's percentage of its own limit; a fleet-wide
// token sum would describe a bucket that does not exist. This module owns that
// arithmetic and the two fallbacks it depends on, so no section has to know how
// a percentage was arrived at in order to render one.

import type { AccountLoad } from './types';

export const TOKEN_COST = 10; // Kalshi default tokens/request (non-default endpoints exist)

// Per-second token limits by tier (read / write). The relay only auto-upgrades
// to Advanced, so Basic/Advanced are the live cases; unknown → assume Basic.
export const TIER_LIMITS: Record<string, { read: number; write: number }> = {
  basic: { read: 200, write: 100 },
  advanced: { read: 300, write: 300 },
};
export function tierLimit(tier?: string) {
  return TIER_LIMITS[tier ?? 'basic'] ?? TIER_LIMITS.basic;
}

export interface Enriched extends AccountLoad {
  readTokS: number;
  writeTokS: number;
  readPct: number;
  writePct: number;
  lim: { read: number; write: number };
  /** true when readTokS/writeTokS are relay-measured per-endpoint tokens
   *  (vs the legacy count × 10 estimate against an older relay). */
  measured: boolean;
}
export function enrich(a: AccountLoad): Enriched {
  const rq = a.requests;
  const winSec = (rq?.windowMs || 10000) / 1000;
  // Prefer the relay's MEASURED per-endpoint token spend; fall back to the
  // count × 10 estimate so the page still works against an older relay
  // mid-deploy.
  const measured = rq?.read != null && rq?.write != null;
  const reads = (rq?.byBucket?.critical ?? 0) + (rq?.byBucket?.general ?? 0);
  const writes = rq?.byBucket?.trading ?? 0;
  const readTokS = measured ? rq.read!.tokensPerSecond : (reads / winSec) * TOKEN_COST;
  const writeTokS = measured ? rq.write!.tokensPerSecond : (writes / winSec) * TOKEN_COST;
  // Denominator: Kalshi's own sampled refill rates when the ground-truth
  // sampler has run; the tier-map guess otherwise.
  const sampled = a.limits?.parsed;
  const guess = tierLimit(a.tier);
  const lim = {
    read: sampled?.read?.refillRate ?? guess.read,
    write: sampled?.write?.refillRate ?? guess.write,
  };
  return {
    ...a,
    readTokS,
    writeTokS,
    readPct: Math.round((readTokS / lim.read) * 100),
    writePct: Math.round((writeTokS / lim.write) * 100),
    lim,
    measured,
  };
}

/** Text colour for a load percentage. `throttled` forces danger regardless of
 *  the rate: an account in 429 backoff is unhealthy even while its measured
 *  spend has dropped to nothing, which is precisely what backoff causes. */
export function loadTone(pct: number, throttled = false): string {
  if (throttled || pct >= 100) return 'text-red-400';
  if (pct >= 75) return 'text-amber-400';
  return 'text-foreground';
}
